import { create } from 'zustand'
import { createSighting } from '@/services/api/sightings'
import { applyDescription } from '@/features/cats/attributes'
import { storeMediaEmbedding } from '@/services/api/identity'
import { attachMedia, mediaStorage } from '@/services/media/provider'
import { IDENTITY_MODEL } from '@/app/config'
import { supabase } from '@/services/supabase/client'
import {
  adoptOrphanDrafts,
  countPending,
  deleteDraft,
  listDrafts,
  patchDraft,
  putDraft,
  type CaptureDraft,
} from './db'

/** Quién ha entrado ahora mismo, o null. Es lo que decide qué borradores son suyos. */
async function cuentaActual(): Promise<string | null> {
  const { data } = await supabase.auth.getSession()
  return data.session?.user.id ?? null
}

interface QueueStore {
  pending: number
  syncing: boolean
  online: boolean
  refresh: () => Promise<void>
  sync: () => Promise<void>
}

export const useQueue = create<QueueStore>((set, get) => ({
  pending: 0,
  syncing: false,
  online: typeof navigator === 'undefined' ? true : navigator.onLine,

  refresh: async () => {
    set({ pending: await countPending(await cuentaActual()) })
  },

  sync: async () => {
    if (get().syncing || !navigator.onLine) return
    const yo = await cuentaActual()
    if (!yo) return

    set({ syncing: true })
    try {
      // Solo los de esta cuenta: los de otra persona que usó este móvil se
      // quedan esperando a su dueño, no se suben a nombre de quien entró. (§H19)
      const drafts = (await listDrafts(yo)).filter((d) => d.status !== 'syncing')
      for (const draft of drafts) {
        // Give up on a draft that keeps failing rather than looping forever;
        // it stays visible so the person can retry it by hand.
        if (draft.attempts >= 6) continue
        await syncDraft(draft)
      }
    } finally {
      set({ syncing: false, pending: await countPending(yo) })
    }
  },
}))

/**
 * Replays one capture.
 *
 * Every step records what it achieved, so a sync interrupted halfway resumes
 * instead of starting over — and because create_sighting keys off the draft id,
 * calling it twice produces one encounter, not two. (§74)
 */
export async function syncDraft(draft: CaptureDraft): Promise<void> {
  await patchDraft(draft.id, { status: 'syncing' })
  let state = { ...draft }

  try {
    if (!state.sightingId) {
      const result = await createSighting({
        syncClientId: state.id,
        catId: state.catId,
        observedAt: state.observedAt,
        exactLat: state.fix?.lat ?? null,
        exactLng: state.fix?.lng ?? null,
        gpsAccuracy: state.fix?.accuracy ?? null,
        newCatName: state.newCatName,
        personalName: state.personalName,
        sex: state.sex,
        notes: state.notes,
        city: state.city,
        region: state.region,
        country: state.country,
        note: state.note,
      })
      state = { ...state, sightingId: result.sighting_id, resolvedCatId: result.cat_id }
      await patchDraft(state.id, {
        sightingId: result.sighting_id,
        resolvedCatId: result.cat_id,
      })

      // Whatever the description engine read, recorded as a suggestion the
      // person can overrule. (§21)
      if (state.description && result.is_new_cat) {
        await applyDescription(result.user_cat_id, state.description)
      }
    }

    if (!state.uploaded) {
      // The placeholder row is kept across retries: a second attempt re-signs
      // the same key rather than leaving another pending row behind.
      const ticket = await mediaStorage.requestUpload({
        kind: 'image',
        mimeType: state.mimeType,
        sizeBytes: state.photo.size,
        catId: state.resolvedCatId ?? null,
        sightingId: state.sightingId ?? null,
        width: state.width,
        height: state.height,
        mediaId: state.mediaId ?? null,
      })
      if (ticket.mediaId !== state.mediaId) {
        state = { ...state, mediaId: ticket.mediaId }
        await patchDraft(state.id, { mediaId: ticket.mediaId })
      }
      await mediaStorage.upload(ticket, state.photo, state.thumbnail)
      await mediaStorage.finalize({
        mediaId: ticket.mediaId,
        sizeBytes: state.photo.size,
        width: state.width,
        height: state.height,
      })
      state = { ...state, uploaded: true }
      await patchDraft(state.id, { uploaded: true })
    }

    if (state.resolvedCatId && state.mediaId) {
      await attachMedia(state.mediaId, state.resolvedCatId, state.sightingId)

      if (state.embedding && state.embedding.length > 0) {
        await storeMediaEmbedding({
          mediaId: state.mediaId,
          embedding: state.embedding,
          modelVersion: IDENTITY_MODEL,
          quality: state.quality ?? undefined,
          catId: state.resolvedCatId,
        })
      }
    }

    await deleteDraft(state.id)
  } catch (error) {
    await patchDraft(draft.id, {
      status: 'failed',
      attempts: draft.attempts + 1,
      lastError: error instanceof Error ? error.message : 'sync_failed',
    })
  }
}

export async function queueDraft(draft: CaptureDraft): Promise<void> {
  // Se sella con su dueño al guardarlo, que es cuando se sabe seguro de quién es.
  await putDraft({ ...draft, ownerId: draft.ownerId ?? (await cuentaActual()) ?? undefined })
  await useQueue.getState().refresh()
  void useQueue.getState().sync()
}

/** Retries one draft on demand, from the pending-sync screen. */
export async function retryDraft(id: string): Promise<void> {
  await patchDraft(id, { status: 'pending', attempts: 0, lastError: undefined })
  await useQueue.getState().sync()
}

export async function discardDraft(id: string): Promise<void> {
  await deleteDraft(id)
  await useQueue.getState().refresh()
}

/**
 * Al entrar una cuenta: se adjudica los borradores de antes de que llevaran
 * dueño y se pone al día el contador. Al salir, el contador vuelve a cero —
 * los borradores NO se borran: son de alguien, y volverán a verse cuando entre.
 */
export async function onAccountChange(userId: string | null): Promise<void> {
  if (userId) await adoptOrphanDrafts(userId).catch(() => 0)
  await useQueue.getState().refresh()
  if (userId) void useQueue.getState().sync()
}

export function initQueue() {
  const store = useQueue.getState()
  void store.refresh()

  const goOnline = () => {
    useQueue.setState({ online: true })
    void useQueue.getState().sync()
  }
  const goOffline = () => useQueue.setState({ online: false })

  window.addEventListener('online', goOnline)
  window.addEventListener('offline', goOffline)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') void useQueue.getState().sync()
  })

  if (navigator.onLine) void store.sync()
}
